import { useRef } from "react";

import { ACTIVITY_LABELS, ACTIVITY_CATEGORIES, formatNumber, relativeDays } from "./licensingTheme";
import { ChartTooltip, useChartTooltip } from "./ChartTooltip";

const CATEGORY_ORDER = ["trials", "payments", "cancellations", "failures", "refunds"];

/**
 * What has been happening, school by school.
 *
 * Opens on the schools rather than the events: "Sarasota — 12 failures this
 * quarter" is something an operator can act on, a scrolling list of forty
 * individual lines is not. Each card opens that school's events as a table;
 * each count on a card opens only that kind of event.
 */
export default function ActivityBySchool({ summary, loading, error, onDrill }) {
  const scope = useRef(null);
  const { tip, bind, hide } = useChartTooltip();
  const rows = summary?.rows ?? [];
  const windowDays = summary?.windowDays ?? 90;

  const totals = rows.reduce(
    (acc, row) => {
      CATEGORY_ORDER.forEach((c) => {
        acc[c] += row[c] ?? 0;
      });
      acc.events += row.events ?? 0;
      return acc;
    },
    { events: 0, trials: 0, payments: 0, cancellations: 0, failures: 0, refunds: 0 },
  );

  const open = (row, category) => {
    hide();
    const name = row ? row.schoolName || "No school linked" : "All schools";
    const scopeFilters = !row ? {} : row.schoolId ? { schoolId: String(row.schoolId) } : { noSchool: "true" };
    onDrill({
      type: "activity",
      title: category ? `${name} · ${ACTIVITY_CATEGORIES[category].label}` : `${name} · activity`,
      filters: { ...scopeFilters, ...(category ? { category } : {}) },
    });
  };

  const card = (row) => {
    const data = row ?? totals;
    const max = Math.max(1, ...CATEGORY_ORDER.map((c) => data[c] ?? 0));
    return (
      <article key={row ? row.schoolId ?? "none" : "all"} className={`mcap-lic-activity-card ${row ? "" : "is-total"}`}>
        <button type="button" className="mcap-lic-activity-head" onClick={() => open(row)}>
          <span className="mcap-lic-activity-name">{row ? row.schoolName || "No school linked" : "All schools"}</span>
          <span className="mcap-lic-activity-count">
            {formatNumber(data.events)}
            <small> event{data.events === 1 ? "" : "s"}</small>
          </span>
          <span className="mcap-lic-activity-last">
            {row
              ? `Latest: ${ACTIVITY_LABELS[row.lastAction] || row.lastAction} · ${relativeDays(row.lastAt)}`
              : `${formatNumber(rows.length)} school${rows.length === 1 ? "" : "s"} · last ${windowDays} days`}
          </span>
        </button>
        <ul className="mcap-lic-activity-cats">
          {CATEGORY_ORDER.map((category) => {
            const value = data[category] ?? 0;
            const meta = ACTIVITY_CATEGORIES[category];
            return (
              <li key={category}>
                <button
                  type="button"
                  disabled={!value}
                  onClick={() => open(row, category)}
                  {...bind({
                    title: meta.label,
                    subtitle: meta.detail,
                    rows: [{ label: "Events", value: formatNumber(value), color: meta.color }],
                    footer: value ? "Click to see who" : undefined,
                  })}
                >
                  <span className="mcap-lic-activity-cat-label">{meta.label}</span>
                  <span className="mcap-lic-activity-cat-bar" aria-hidden="true">
                    <span style={{ width: `${(value / max) * 100}%`, background: meta.color }} />
                  </span>
                  <span className="mcap-lic-activity-cat-value">{formatNumber(value)}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </article>
    );
  };

  return (
    <section className="mcap-lic-panel" aria-labelledby="mcap-lic-activity-title" ref={scope}>
      <header className="mcap-lic-panel-head">
        <div>
          <h3 id="mcap-lic-activity-title" className="mcap-lic-panel-title">
            Activity by school
          </h3>
          <p className="mcap-lic-panel-sub">
            Last {windowDays} days. Open a school to see every event.
          </p>
        </div>
      </header>

      {error && <p className="mcap-lic-error">The activity summary could not load.</p>}
      {!error && !loading && rows.length === 0 && <p className="mcap-lic-empty">Nothing happened in the last {windowDays} days.</p>}

      {!error && (loading || rows.length > 0) && (
        <div className="mcap-lic-activity-grid">
          {loading
            ? Array.from({ length: 4 }, (_, i) => <article key={i} className="mcap-lic-activity-card is-skeleton" />)
            : [card(null), ...rows.map((row) => card(row))]}
        </div>
      )}

      <ChartTooltip tip={tip} />
    </section>
  );
}
